import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  getActiveDay,
  getClockStateOn,
  hasMissingClockOut,
  sortEventsByTime,
  useTimeclockStore,
  type ClockState,
} from '../../store/useTimeclockStore';
import { useToastStore } from '../../store/useToastStore';
import { Clock, LogIn, LogOut, Coffee, Play, AlertTriangle } from 'lucide-react';
import { formatUtcToLocalTime, isoDate } from '@/utils/dateUtils';
import { displayDayMinutes, formatDuration } from '@/utils/timeclockTotals';
import { StampTime } from '@/components/Shared/StampTime';
import { useClockOutGuard } from '@/hooks/useClockOutGuard';
import { useClockInWindow } from '@/hooks/useClockInWindow';
import { ClockStampDialogs } from '@/components/Dashboard/Employee/ClockStampDialogs';
import { TripActions } from '@/components/Dashboard/Employee/TripActions';
import { useClockStamp } from '@/hooks/useClockStamp';
import {
  FacilityFlowCoreModulesPlanningEntitiesTimeClockEventType as TimeClockEventTypeEnum,
} from '@/api/generated/models';

const STATUS_COLOR: Record<ClockState, string> = {
  in: 'text-[#00A871]',
  break: 'text-amber-600',
  out: 'text-slate-500',
  idle: 'text-slate-400',
};

const STATUS_BG: Record<ClockState, string> = {
  in: 'bg-emerald-50 border-emerald-200',
  break: 'bg-amber-50 border-amber-200',
  out: 'bg-slate-50 border-slate-200',
  idle: 'bg-slate-50 border-slate-200',
};

const STATUS_PILL: Record<ClockState, string> = {
  in: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  break: 'bg-amber-50 text-amber-700 border border-amber-200',
  out: 'bg-slate-100 text-slate-600',
  idle: 'bg-amber-50 text-amber-700 border border-amber-200',
};

const STATUS_LABEL_KEY: Record<ClockState, string> = {
  in: 'timeclock.statusClockedIn',
  break: 'timeclock.statusOnBreak',
  out: 'timeclock.statusClockedOut',
  idle: 'timeclock.statusIdle',
};

export function TimeclockWidget() {
  const { t } = useTranslation('header');
  const {
    todayEntry,
    openDays,
    loading,
    fetchToday,
    fetchOpenDays,
    recordEvent,
    error,
    clearError,
    successMessage,
  } = useTimeclockStore();
  const { addToast } = useToastStore();
  const [showDetails, setShowDetails] = useState(false);
  const clockInWindow = useClockInWindow();
  const clockInAllowed = clockInWindow.allowed;
  // why: the widget sits in the header all day, so a value memoised once would still say
  // "yesterday" after midnight.
  const [todayIso, setTodayIso] = useState(() => isoDate(new Date()));
  // Ticks the running work time once a minute.
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const checkInterval = setInterval(() => {
      const current = new Date();
      setTodayIso(isoDate(current));
      setNow(current);
    }, 60_000);
    return () => clearInterval(checkInterval);
  }, []);

  // Reloaded after midnight as well: a night shift then continues on yesterday's open day.
  useEffect(() => {
    fetchToday();
    fetchOpenDays();
  }, [todayIso, fetchToday, fetchOpenDays]);

  // Refreshed whenever the dropdown opens, so a forgotten clock-out blocks the button in time.
  useEffect(() => {
    if (showDetails) fetchOpenDays();
  }, [showDetails, fetchOpenDays]);

  useEffect(() => {
    if (error) {
      addToast(error, 'error');
      clearError();
    }
  }, [error, addToast, clearError]);

  useEffect(() => {
    if (successMessage) {
      addToast(successMessage, 'success');
      clearError();
    }
  }, [successMessage, addToast, clearError]);

  // Clock-in and clock-out ask for the work location and the vehicle of the day first.
  const clockStamp = useClockStamp();
  // A night shift past midnight stays on its own day until it is clocked out (FACF-149).
  const activeDay = getActiveDay(todayEntry, openDays, todayIso);

  const handleClockIn = async () => {
    try {
      await clockStamp.stamp('clockIn');
      setShowDetails(false);
    } catch {
      // Error handled by store
    }
  };

  const performClockOut = async () => {
    try {
      await clockStamp.stamp('clockOut', activeDay.date);
    } catch {
      // Error handled by store
    }
  };

  // The same non-blocking checklist warning the dashboard and the time clock page use — this
  // header path used to skip it, which is how open items went unnoticed (ADR-0017).
  const clockOutGuard = useClockOutGuard(todayIso, performClockOut);

  const handleClockOut = async () => {
    try {
      await clockOutGuard.guardClockOut();
    } finally {
      setShowDetails(false);
    }
  };

  const handleBreakStart = async () => {
    try {
      await recordEvent('breakStart', activeDay.date);
    } catch {
      // Error handled by store
    }
  };

  const handleBreakEnd = async () => {
    try {
      await recordEvent('breakEnd', activeDay.date);
    } catch {
      // Error handled by store
    }
  };

  const status = getClockStateOn(activeDay.entry, activeDay.date);
  // Clocking in again after a clock-out is allowed, just like on the time clock page.
  const canClockIn = status === 'idle' || status === 'out';
  const clockInBlocked = hasMissingClockOut(openDays, todayIso);
  const statusColor = STATUS_COLOR[status];
  const pulseClass = status === 'in' || status === 'break' ? 'animate-pulse' : '';

  // First clock-in and last clock-out of the day, in time order.
  const orderedEvents = sortEventsByTime(activeDay.entry?.events);
  const workMinutes = activeDay.entry ? displayDayMinutes(activeDay.entry, now).workMinutes : 0;
  const clockInEvent = orderedEvents.find((e) => e.eventType === TimeClockEventTypeEnum.NUMBER_1);
  const clockOutEvent = [...orderedEvents]
    .reverse()
    .find((e) => e.eventType === TimeClockEventTypeEnum.NUMBER_2);

  return (
    <div className="relative">
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md border transition-colors ${STATUS_BG[status]} hover:bg-slate-50`}
        title={t(STATUS_LABEL_KEY[status])}
        aria-label={t(STATUS_LABEL_KEY[status])}
      >
        <div className={`relative ${pulseClass}`}>
          <Clock className={`w-4 h-4 ${statusColor}`} strokeWidth={2} />
          {status === 'in' && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-500 rounded-full" />
          )}
        </div>
        {/* Visible on phones too — employees stamp mostly from there (FACF-107). */}
        <span className={`text-[11px] font-medium tabular-nums ${statusColor}`}>
          {status === 'in'
            ? formatUtcToLocalTime(clockInEvent?.effectiveTimestamp ?? clockInEvent?.timestamp)
            : status === 'break'
              ? t('timeclock.statusOnBreak')
              : status === 'out'
                ? t('timeclock.done')
                : t('timeclock.idle')}
        </span>
      </button>

      {/* Dropdown */}
      {showDetails && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowDetails(false)} />
          <div
            className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-slate-200 py-3 z-20"
            style={{ boxShadow: '0 10px 30px -10px rgba(15,23,42,0.15)' }}
          >
            <div className="px-4 pb-3 border-b border-slate-100">
              <p className="text-[15px] font-semibold text-slate-900 tracking-tight">{t('timeclock.title')}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {new Date().toLocaleDateString('de-DE', {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>

            <div className="px-4 py-3 space-y-2">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-500">{t('timeclock.status')}</span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${STATUS_PILL[status]}`}
                >
                  {t(STATUS_LABEL_KEY[status])}
                </span>
              </div>

              {/* Times */}
              {clockInEvent?.timestamp && (
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-500">
                    {activeDay.isRunningShift ? t('timeclock.startYesterday') : t('timeclock.start')}
                  </span>
                  <StampTime
                    event={clockInEvent}
                    className="text-[13px] font-medium text-slate-900"
                  />
                </div>
              )}
              {clockOutEvent?.timestamp && (
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-500">{t('timeclock.end')}</span>
                  <StampTime
                    event={clockOutEvent}
                    className="text-[13px] font-medium text-slate-900"
                  />
                </div>
              )}

              {/* Work time — counted like the time clock page, running while clocked in. */}
              {status !== 'idle' && (
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-500">{t('timeclock.workTime')}</span>
                  <span
                    className={`text-[13px] font-medium tabular-nums ${status === 'in' ? 'text-[#00A871]' : 'text-slate-900'}`}
                  >
                    {formatDuration(workMinutes)}
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="px-4 pt-2 border-t border-slate-100 space-y-2">
              {canClockIn && clockInBlocked && (
                <p className="flex items-start gap-1.5 text-[11px] text-amber-700">
                  <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" strokeWidth={2} />
                  <span>
                    {t('timeclock:openDays.blocksClockIn')}{' '}
                    <Link
                      to="/timeclock"
                      onClick={() => setShowDetails(false)}
                      className="font-medium underline"
                    >
                      {t('timeclock:openDays.goToTimeclock')}
                    </Link>
                  </span>
                </p>
              )}

              {canClockIn && !clockInAllowed && (
                <p className="text-[11px] text-amber-600">{t('timeclock.outsideHours', { start: clockInWindow.start, end: clockInWindow.end })}</p>
              )}

              {canClockIn && clockInWindow.beforeWorkCount && (
                <p className="text-[11px] text-sky-600">
                  {t('timeclock.earlyClockInHint', { time: clockInWindow.workCountStart })}
                </p>
              )}

              {canClockIn && (
                <button
                  onClick={handleClockIn}
                  disabled={loading || clockStamp.preparing || !clockInAllowed || clockInBlocked}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#00A871] hover:bg-emerald-700 text-white rounded-md text-[12px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LogIn className="w-4 h-4" strokeWidth={2} />
                  {loading ? t('timeclock.loading') : t('timeclock.clockIn')}
                </button>
              )}

              {status === 'in' && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleBreakStart}
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-[12px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Coffee className="w-4 h-4" strokeWidth={2} />
                    {t('timeclock.breakStart')}
                  </button>
                  <button
                    onClick={handleClockOut}
                    disabled={loading || clockOutGuard.checking || clockStamp.preparing}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-md text-[12px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <LogOut className="w-4 h-4" strokeWidth={2} />
                    {clockOutGuard.checking
                      ? t('timeclock.checkingChecklists')
                      : t('timeclock.clockOutShort')}
                  </button>
                </div>
              )}

              {status === 'break' && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleBreakEnd}
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#00A871] hover:bg-emerald-700 text-white rounded-md text-[12px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Play className="w-4 h-4" strokeWidth={2} />
                    {loading ? t('timeclock.loading') : t('timeclock.breakEnd')}
                  </button>
                  <button
                    onClick={handleClockOut}
                    disabled={loading || clockOutGuard.checking || clockStamp.preparing}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-md text-[12px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <LogOut className="w-4 h-4" strokeWidth={2} />
                    {clockOutGuard.checking
                      ? t('timeclock.checkingChecklists')
                      : t('timeclock.clockOutShort')}
                  </button>
                </div>
              )}
              {/* Trip menu (FAFD-68); its dialogs portal to body, so the dropdown stays open meanwhile. */}
              <TripActions clockedIn={status === 'in' || status === 'break'} />
            </div>
          </div>
        </>
      )}

      {/* Sibling of the dropdown, never inside it: closing the dropdown must not unmount the
          dialog. Modal portals to body at z-[80], well above the header. */}
      <ClockStampDialogs clockStamp={clockStamp} clockOutGuard={clockOutGuard} todayIso={todayIso} busy={loading} />
    </div>
  );
}
